import { Accessor, createEffect, createSignal, onCleanup } from "solid-js";
import type { Channel } from "stoat.js";

import { CONFIGURATION } from "@revolt/common";

import { oldEnough } from "./ageCheck";
import type { DirectResult } from "./directStream";

/**
 * Konkord: our own voice infrastructure for allowlisted Stoat servers (see services/konkord-voice).
 * Accounts and chat stay on Stoat; voice and screen share for these servers run on our LiveKit,
 * falling back to Stoat's voice whenever ours is unavailable.
 */

export type VoiceProvider = "stoat" | "konkord";

/**
 * Konkord: the same objects for calls and people that didn't change, so the
 * lists showing them keep their rows (each poll used to rebuild every row of
 * every call in the sidebar, avatars included, every 5 s)
 */
export function keepUnchanged(
  previous: Record<string, RoomInfo>,
  next: Record<string, RoomInfo>,
): Record<string, RoomInfo> {
  const same = (a: unknown, b: unknown) =>
    JSON.stringify(a) === JSON.stringify(b);
  let changed = Object.keys(previous).length !== Object.keys(next).length;
  const result: Record<string, RoomInfo> = {};
  for (const [id, room] of Object.entries(next)) {
    const before = previous[id];
    if (before && same(before, room)) {
      result[id] = before;
      continue;
    }
    changed = true;
    const people = new Map(before?.participants.map((p) => [p.id, p]));
    result[id] = {
      ...room,
      participants: room.participants.map((person) => {
        const old = people.get(person.id);
        return old && same(old, person) ? old : person;
      }),
    };
  }
  return changed ? result : previous;
}

/**
 * Konkord: a game someone plays (found by the desktop app)
 */
/**
 * Konkord: a soundboard sound someone saved on our server
 */
export type SavedSound = { id: string; name: string; at: number };

export type ActivityGame = {
  key: string;
  name: string;
  steamAppId?: number;
  /** small PNG data URL of the program's icon */
  icon?: string;
  /** Steam header art our gateway found (friends' activity only) */
  cover?: string;
};

/**
 * Konkord: friends' games, now and over the last 7 days
 */
export type FriendsActivity = {
  now: { user: string; game: ActivityGame; since: number }[];
  week: {
    game: ActivityGame;
    ms: number;
    players: { user: string; ms: number }[];
  }[];
};

/**
 * Who is in a call on our voice server (Stoat doesn't know about these calls)
 */
export type RoomInfo = {
  /** When the call started (ms since epoch) */
  since: number;
  participants: {
    id: string;
    /** Konkord: when they joined (ms since epoch, newer gateways) */
    joinedAt?: number;
    streaming: boolean;
    camera: boolean;
    muted: boolean;
    /** Konkord: only sent by newer gateways */
    deafened?: boolean;
    /** Konkord: streamer ids this person watches (newer gateways) */
    watching?: string[];
  }[];
  /** Stream previews by streamer identity (JPEG data URLs) */
  thumbnails: Record<string, string>;
};

// Konkord: 5 s so someone who dropped leaves the preview quickly (the gateway
// shares a 3 s cache between clients)
const ROOMS_POLL_MS = 5_000;

/**
 * After leaving a call, hide ourselves from its preview for this long: the
 * gateway caches its room list and LiveKit drops us a moment later
 */
const LEFT_GRACE_MS = 15_000;

type GatewayStatus = "disabled" | "online" | "offline";

const POLL_MS = 15_000;
const REQUEST_TIMEOUT_MS = 5000;

/**
 * Healthy checks in a row before the gateway counts as online again after an outage,
 * so a flapping server doesn't bounce everyone between providers
 */
const ONLINE_AFTER = 2;

/**
 * Client side backoff after this client failed to use our voice server
 * (e.g. its network blocks our ports while the gateway still reports healthy)
 */
const BACKOFF_START_MS = 30_000;
const BACKOFF_MAX_MS = 10 * 60_000;

/**
 * The gateway receives the Stoat session token, only talk to it over HTTPS (or locally)
 */
function gatewayUrl(raw?: string) {
  if (!raw) return;
  try {
    const url = new URL(raw);
    const local = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
    if (url.protocol === "https:" || (url.protocol === "http:" && local)) {
      return url.href.replace(/\/+$/, "");
    }
  } catch {
    // fall through
  }
  console.error("[konkord] VITE_KONKORD_VOICE_URL must be an https URL");
}

class KonkordVoiceGateway {
  readonly url = gatewayUrl(CONFIGURATION.KONKORD_VOICE_URL);

  status: Accessor<GatewayStatus>;
  #setStatus: (status: GatewayStatus) => void;

  servers: Accessor<ReadonlySet<string>>;
  #setServers: (servers: ReadonlySet<string>) => void;

  #healthyChecks = 0;
  #everOnline = false;
  #pollGen = 0;
  #polling?: Promise<void>;
  /**
   * Konkord: resolves once the first /status answered (or failed)
   */
  #firstPoll?: Promise<void>;
  /**
   * Konkord: channels where the gateway refused us a token (no permission,
   * not a voice channel): their calls stay on Stoat for this session
   */
  #denied: Accessor<ReadonlySet<string>>;
  #setDenied: (denied: ReadonlySet<string>) => void;

  cooling: Accessor<boolean>;
  #setCooling: (cooling: boolean) => void;
  #backoff = BACKOFF_START_MS;
  #cooldownTimer?: ReturnType<typeof setTimeout>;

  rooms: Accessor<Record<string, RoomInfo>>;
  #setRooms: (rooms: Record<string, RoomInfo>) => void;
  #watchedRooms = new Map<string, number>();
  #roomsSoon?: ReturnType<typeof setTimeout>;
  #left = new Map<string, { identity: string; until: number }>();
  #getAuthHeader?: () => [string, string];

  /**
   * Konkord: the age check (birth date told once, calls from 14), undefined
   * until our server answered
   */
  age: Accessor<AgeInfo | undefined>;
  #setAge: (age: AgeInfo | undefined) => void;
  /** Konkord: something asked for the birth date (e.g. joining a call) */
  askAge: Accessor<boolean>;
  setAskAge: (ask: boolean) => void;

  /**
   * Konkord: VIP on our server's list: streams up to 1440p 60 (everyone
   * else 720p or 1080p at 30). Only the streamer needs it.
   */
  vip: Accessor<boolean>;
  #setVip: (vip: boolean) => void;

  constructor() {
    const [vip, setVip] = createSignal(false);
    this.vip = vip;
    this.#setVip = setVip;
    const [age, setAge] = createSignal<AgeInfo>();
    this.age = age;
    this.#setAge = setAge;
    const [askAge, setAskAge] = createSignal(false);
    this.askAge = askAge;
    this.setAskAge = setAskAge;
    const [status, setStatus] = createSignal<GatewayStatus>(
      this.url ? "offline" : "disabled",
    );
    this.status = status;
    this.#setStatus = setStatus;

    const [servers, setServers] = createSignal<ReadonlySet<string>>(new Set());
    this.servers = servers;
    this.#setServers = setServers;

    const [cooling, setCooling] = createSignal(false);
    this.cooling = cooling;
    this.#setCooling = setCooling;

    const [denied, setDenied] = createSignal<ReadonlySet<string>>(new Set());
    this.#denied = denied;
    this.#setDenied = setDenied;

    const [rooms, setRooms] = createSignal<Record<string, RoomInfo>>({});
    this.rooms = rooms;
    this.#setRooms = setRooms;

    if (this.url && typeof window !== "undefined") {
      this.#firstPoll = this.poll();
      setInterval(() => void this.poll(), POLL_MS);
      setInterval(() => void this.pollRooms(), ROOMS_POLL_MS);
      window.addEventListener("online", () => void this.poll());
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) void this.pollRooms();
      });
    }
  }

  private poll(immediate = false): Promise<void> {
    if (this.#polling) return this.#polling;
    this.#polling = this.#poll(immediate).finally(() => {
      this.#polling = undefined;
    });
    return this.#polling;
  }

  /**
   * @param immediate online after one healthy check (the user asked to retry)
   */
  async #poll(immediate: boolean) {
    const gen = this.#pollGen;

    try {
      const response = await fetch(`${this.url}/status`, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (!response.ok) throw new Error(`status ${response.status}`);

      const body = (await response.json()) as {
        online?: unknown;
        servers?: unknown;
      };

      // Results from before a markOffline() don't count as healthy checks
      if (gen !== this.#pollGen) return;

      if (Array.isArray(body.servers)) {
        this.#setServers(
          new Set(body.servers.filter((id) => typeof id === "string")),
        );
      }

      if (body.online !== true) {
        this.markOffline();
        return;
      }

      this.#healthyChecks++;
      if (
        immediate ||
        !this.#everOnline ||
        this.#healthyChecks >= ONLINE_AFTER
      ) {
        this.#everOnline = true;
        // Konkord: failures during an outage (e.g. a gateway update) say
        // nothing about now: come back right away, not after a long backoff
        if (this.status() === "offline") {
          clearTimeout(this.#cooldownTimer);
          this.#setCooling(false);
          this.#backoff = BACKOFF_START_MS;
        }
        this.#setStatus("online");
      }
    } catch {
      this.markOffline();
    }
  }

  /**
   * Konkord: wait (briefly) for the first status, so a call joined right after
   * opening the app doesn't land on Stoat while ours is up
   */
  async ready(timeoutMs = 1500) {
    if (!this.#firstPoll) return;
    await Promise.race([
      this.#firstPoll,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  /**
   * Konkord: whether the gateway hasn't refused us this channel
   */
  allowed(channelId?: string) {
    return !!channelId && !this.#denied().has(channelId);
  }

  /**
   * Mark our voice server as unavailable until it passes health checks again
   */
  markOffline() {
    if (!this.url) return;
    this.#pollGen++;
    this.#healthyChecks = 0;
    this.#setStatus("offline");
  }

  /**
   * This client failed to use our voice server, avoid it for a while (exponential backoff)
   */
  reportFailure() {
    if (!this.url) return;
    clearTimeout(this.#cooldownTimer);
    this.#setCooling(true);
    this.#cooldownTimer = setTimeout(
      () => this.#setCooling(false),
      this.#backoff,
    );
    this.#backoff = Math.min(this.#backoff * 2, BACKOFF_MAX_MS);
  }

  /**
   * The user asked to try our voice server again: forget earlier failures and
   * check it right away
   */
  async retryNow() {
    if (!this.url) return;
    clearTimeout(this.#cooldownTimer);
    this.#setCooling(false);
    this.#backoff = BACKOFF_START_MS;
    this.#setDenied(new Set());
    // a poll already running may predate the server coming back, check again
    await this.#polling;
    await this.poll(true);
  }

  /**
   * This client has been in a stable call on our voice server
   */
  reportSuccess() {
    this.#backoff = BACKOFF_START_MS;
  }

  /**
   * Session used for room previews and thumbnails
   */
  setAuth(getAuthHeader: () => [string, string]) {
    this.#getAuthHeader = getAuthHeader;
  }

  #session() {
    const [header, token] = this.#getAuthHeader?.() ?? [];
    return header === "X-Session-Token" && token ? token : undefined;
  }

  /**
   * Konkord: ask our server for the user's birth date and whether they may
   * join calls
   */
  async loadAge() {
    const token = this.#session();
    if (!this.url || !token) return;
    try {
      const response = await fetch(`${this.url}/age`, {
        redirect: "error",
        headers: { "X-Session-Token": token },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (response.ok) this.#setAge(parseAge(await response.json()));
    } catch {
      // unknown for now: our server checks again when joining a call
    }
  }

  /**
   * Konkord: answer the age question from the birth date ("YYYY-MM-DD"). The
   * date stays on this device: our server only hears yes or no
   */
  async saveBirthdate(birthdate: string) {
    const allowed = oldEnough(birthdate, this.age()?.minimum ?? 14);
    if (allowed === undefined) throw new Error("invalid date");
    const token = this.#session();
    if (!this.url || !token) throw new Error("not signed in");
    const response = await fetch(`${this.url}/age`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ allowed }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (response.status === 409) {
      await this.loadAge();
      throw new Error("no corrections left");
    }
    if (!response.ok) throw new Error(`age ${response.status}`);
    const age = parseAge(await response.json());
    this.#setAge(age);
    return age;
  }

  /**
   * Konkord: anonymous counts (no address, nothing about who): a direct
   * stream link that worked or failed, or a network test verdict
   */
  async #count(kind: "direct" | "network", body: object) {
    const token = this.#session();
    if (!this.url || !token) return;
    await fetch(`${this.url}/stats/${kind}`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    }).catch(() => {});
  }

  /**
   * Konkord: soundboard sounds saved by these users (people in the call)
   */
  async listSounds(users: string[]): Promise<Record<string, SavedSound[]>> {
    const token = this.#session();
    if (!this.url || !token || !users.length) return {};
    const response = await fetch(`${this.url}/sounds/list`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ users: users.slice(0, 50) }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) throw new Error(`sounds ${response.status}`);
    const body = await response.json();
    const result: Record<string, SavedSound[]> = {};
    for (const user of users) {
      const list = body?.sounds?.[user];
      if (!Array.isArray(list)) continue;
      result[user] = list.filter(
        (s): s is SavedSound =>
          typeof s?.id === "string" &&
          /^[A-Za-z0-9_-]{16}$/.test(s.id) &&
          typeof s?.name === "string",
      );
    }
    return result;
  }

  /**
   * Konkord: save a soundboard sound (audio file as base64)
   */
  async addSound(name: string, audio: string): Promise<SavedSound> {
    const token = this.#session();
    if (!this.url || !token) throw new Error("not signed in");
    const response = await fetch(`${this.url}/sounds/add`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ name, audio }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS * 3),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok)
      throw new Error(body?.error ?? `sounds ${response.status}`);
    return body.sound as SavedSound;
  }

  /**
   * Konkord: delete one of your soundboard sounds
   */
  async removeSound(id: string) {
    const token = this.#session();
    if (!this.url || !token) throw new Error("not signed in");
    const response = await fetch(`${this.url}/sounds/remove`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ id }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) throw new Error(`sounds ${response.status}`);
  }

  /**
   * Konkord: where a saved soundboard sound is played from
   */
  soundUrl(user: string, id: string) {
    return this.url
      ? `${this.url}/sounds/file/${encodeURIComponent(user)}/${encodeURIComponent(id)}`
      : undefined;
  }

  /**
   * Konkord: get the Konkord role in the Konkord servers we are in (their
   * channels show only to it); true once our server answered
   */
  async claimMember(): Promise<{ ok: boolean; starter?: string }> {
    const token = this.#session();
    if (!this.url || !token) return { ok: false };
    try {
      const response = await fetch(`${this.url}/member`, {
        method: "POST",
        redirect: "error",
        headers: { "X-Session-Token": token },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (!response.ok) return { ok: false };
      const body = (await response.json().catch(() => ({}))) as {
        starter?: unknown;
        vip?: unknown;
      };
      this.#setVip(body.vip === true);
      // the starter group's invite, handed out once per account
      const starter =
        typeof body.starter === "string" &&
        /^[A-Za-z0-9]{4,32}$/.test(body.starter)
          ? body.starter
          : undefined;
      return { ok: true, starter };
    } catch {
      return { ok: false };
    }
  }

  reportDirect(result: DirectResult & { kind: "stream" | "camera" }) {
    return this.#count("direct", result);
  }

  reportNetwork(verdict: string) {
    return this.#count("network", { verdict });
  }

  /**
   * Konkord: the age check before joining a call: asked now if not known
   * yet (right after opening the app), at most a few seconds
   */
  async ensureAge() {
    if (!this.url || this.age()) return;
    await Promise.race([
      this.loadAge(),
      new Promise((resolve) => setTimeout(resolve, REQUEST_TIMEOUT_MS)),
    ]);
  }

  /**
   * Konkord: whether this user may join calls: yes while our server can't
   * be asked (it checks again on its own calls), no without a birth date
   */
  callsAllowed() {
    if (!this.url) return true;
    const age = this.age();
    return !age || age.allowed;
  }

  /**
   * Keep a call preview up to date while something shows it; returns a release function
   */
  watchRoom(channelId: string) {
    this.#watchedRooms.set(
      channelId,
      (this.#watchedRooms.get(channelId) ?? 0) + 1,
    );
    clearTimeout(this.#roomsSoon);
    this.#roomsSoon = setTimeout(() => void this.pollRooms(), 300);

    return () => {
      const count = (this.#watchedRooms.get(channelId) ?? 1) - 1;
      if (count > 0) this.#watchedRooms.set(channelId, count);
      else this.#watchedRooms.delete(channelId);
    };
  }

  /**
   * Fetch call previews again soon (after joining or leaving a call)
   */
  refreshRooms() {
    clearTimeout(this.#roomsSoon);
    this.#roomsSoon = setTimeout(() => void this.pollRooms(), 500);
  }

  /**
   * We just left the call in this channel: drop ourselves from its preview now
   * and keep doing so until the gateway catches up
   */
  markLeft(channelId: string, identity: string) {
    this.#left.set(channelId, { identity, until: Date.now() + LEFT_GRACE_MS });
    this.#setRooms(this.#withoutLeft(this.rooms()));
    // once more after the gateway's cache expired
    setTimeout(() => void this.pollRooms(), 4000);
  }

  #withoutLeft(rooms: Record<string, RoomInfo>) {
    const now = Date.now();
    let result = rooms;
    for (const [channelId, { identity, until }] of this.#left) {
      if (until < now) {
        this.#left.delete(channelId);
        continue;
      }
      const room = result[channelId];
      if (!room?.participants.some((p) => p.id === identity)) continue;
      const participants = room.participants.filter((p) => p.id !== identity);
      result = { ...result };
      if (participants.length) {
        result[channelId] = { ...room, participants };
      } else {
        delete result[channelId];
      }
    }
    return result;
  }

  /**
   * Preview of the call in a channel on our voice server, if any
   */
  room(channelId: string): RoomInfo | undefined {
    return this.rooms()[channelId];
  }

  private async pollRooms() {
    const token = this.#session();
    if (!this.url || !token || document.hidden) return;

    const channels = [...this.#watchedRooms.keys()];
    if (!channels.length) return;

    try {
      const response = await fetch(`${this.url}/rooms`, {
        method: "POST",
        redirect: "error",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": token,
        },
        body: JSON.stringify({ channels }),
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (!response.ok) return;
      const body = (await response.json()) as {
        rooms?: Record<string, RoomInfo>;
      };
      this.#setRooms(
        keepUnchanged(this.rooms(), this.#withoutLeft(body.rooms ?? {})),
      );
    } catch {
      // keep the last known previews
    }
  }

  /**
   * Share a small preview of our screen share with people who can see the channel
   */
  async uploadThumbnail(channelId: string, image: string) {
    const token = this.#session();
    if (!this.url || !token) return;

    await fetch(`${this.url}/thumbnail`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ channel: channelId, image }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  }

  /**
   * Whether the given Stoat server is set up to use Konkord voice
   */
  enabled(serverId?: string) {
    return !!this.url && !!serverId && this.servers().has(serverId);
  }

  /**
   * Whether calls in the given Stoat server currently run on Konkord voice
   */
  active(serverId?: string) {
    return (
      this.enabled(serverId) && this.status() === "online" && !this.cooling()
    );
  }

  /**
   * Ask the gateway for a LiveKit token, proving who we are with the Stoat session
   */
  async token(
    channelId: string,
    [header, sessionToken]: [string, string],
  ): Promise<{ url: string; token: string }> {
    if (header !== "X-Session-Token" || !sessionToken) {
      throw new Error("Konkord voice requires a user session");
    }

    const response = await fetch(`${this.url}/token`, {
      method: "POST",
      // Never replay the session token to another host
      redirect: "error",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": sessionToken,
      },
      body: JSON.stringify({ channel: channelId }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    // Konkord: our server is full: this new call starts on Stoat's (calls
    // already running here still take people), not a server failure
    if (response.status === 503) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (body.error === "Voice server full") throw new VoiceServerFullError();
    }

    // Konkord: no birth date told, or under the minimum age
    if (response.status === 428) {
      void this.loadAge();
      throw new AgeRequiredError();
    }

    if (!response.ok) {
      // refused for good (permissions, not a voice channel, session): don't
      // keep moving a working Stoat call back here after every cooldown
      if (response.status === 401 || response.status === 403) {
        this.#setDenied(new Set([...this.#denied(), channelId]));
      }
      throw new Error(`Konkord voice gateway returned ${response.status}`);
    }

    const body = (await response.json()) as { url?: unknown; token?: unknown };
    if (typeof body.url !== "string" || typeof body.token !== "string") {
      throw new Error("Konkord voice gateway returned an invalid response");
    }

    return { url: body.url, token: body.token };
  }

  /**
   * Konkord: tell friends what we're playing (null when nothing)
   * @param forget Also delete our past games (sharing turned off)
   */
  async postActivity(game: ActivityGame | null, forget = false) {
    const token = this.#session();
    if (!this.url || !token) return;

    const response = await fetch(`${this.url}/activity`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify(forget ? { game, forget } : { game }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) throw new Error(`activity: ${response.status}`);
  }

  /**
   * Konkord: what these friends play now and played in the last 7 days (the
   * gateway only answers for real friends)
   */
  async friendsActivity(friends: string[]) {
    const token = this.#session();
    if (!this.url || !token) return;

    const response = await fetch(`${this.url}/activity/friends`, {
      method: "POST",
      redirect: "error",
      headers: { "Content-Type": "application/json", "X-Session-Token": token },
      body: JSON.stringify({ friends }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) return;
    return (await response.json()) as FriendsActivity;
  }
}

export const konkordVoice = new KonkordVoiceGateway();

export type AgeInfo = {
  /** answered the age question (our server doesn't keep the date) */
  told: boolean;
  minimum: number;
  allowed: boolean;
  /** Corrections of a wrong date still allowed */
  correctionsLeft: number;
};

function parseAge(body: unknown): AgeInfo | undefined {
  const value = body as Partial<AgeInfo> | null;
  if (!value || typeof value.allowed !== "boolean") return;
  return {
    told: value.told === true,
    minimum: typeof value.minimum === "number" ? value.minimum : 14,
    allowed: value.allowed,
    correctionsLeft:
      typeof value.correctionsLeft === "number" ? value.correctionsLeft : 0,
  };
}

/**
 * Konkord: the voice server refused a call until the birth date is told
 * (or the person is under the minimum age)
 */
export class VoiceServerFullError extends Error {
  constructor() {
    super("Konkord voice server full");
  }
}

export class AgeRequiredError extends Error {
  constructor() {
    super("Birth date needed to join calls");
  }
}

/**
 * Konkord: preview of the call in a channel (who is there, streams, since when),
 * kept up to date while the calling component is mounted
 */
export function useKonkordRoom(channel: () => Channel) {
  createEffect(() => {
    const target = channel();
    if (!konkordVoice.enabled(target.serverId)) return;
    onCleanup(konkordVoice.watchRoom(target.id));
  });

  return () => {
    const target = channel();
    return konkordVoice.enabled(target.serverId)
      ? konkordVoice.room(target.id)
      : undefined;
  };
}
